"use client"

import { JSX, useMemo, useState } from "react"
import { scaleBand, scaleLinear, stack, max, format } from "d3"

/**
 * One x-axis category with a value per series.
 *
 * The `values` map is keyed by series key; every key listed in the
 * chart's `seriesKeys` must be present (zero-filled), so the stack generator
 * never encounters an undefined value.
 */
export interface StackedBarDatum {
  /** X-axis label for this bar. */
  label: string
  /** value per series key. Missing keys are treated as 0. */
  values: Record<string, number>
}

/**
 * Props for the StackedBarChart component.
 */
export interface StackedBarChartProps {
  /** Ordered bars, left to right. */
  data: StackedBarDatum[]
  /** Series keys, in stacking order from bottom to top. */
  seriesKeys: string[]
  /**
   * Maps a series key to a stable colour
   */
  colorFor: (seriesKey: string) => string
  /**
   * Formats an x-axis label for display
   * Defaults to the raw label.
   */
  formatXLabel?: (label: string) => string
  /**
   * Formats a series key for display in the tooltip
   */
  formatSeriesLabel?: (seriesKey: string) => string
  /** Rendered height of the chart in pixels. Width is responsive via viewBox. */
  height?: number
  /**
   * Unit suffix appended after the value in the tooltip (e.g. "tokens").
   * Omit for no unit. A leading space is added automatically.
   */
  valueUnit?: string
}

// Fixed inner margins around the plot area (space for axes and labels).
const MARGIN = { top: 8, right: 12, bottom: 28, left: 56 } as const

// The chart is drawn in a fixed coordinate space and scaled to the container
// width by the SVG viewBox, so no runtime measurement is needed.
const VIEWBOX_WIDTH = 640

// Formats a y-axis tick value compactly (e.g. 12000 -> "12k").
const formatTick = format("~s")

const formatExact = format(",")

/**
 * A resolved y-axis tick: its value and its pixel position.
 */
interface Tick {
  value: number
  y: number
}

/**
 * A single rectangle to render.
 */
interface Segment {
  key: string
  seriesKey: string
  label: string
  value: number
  x: number
  y: number
  width: number
  height: number
  fill: string
}

/**
 * Tooltip state: which segment is hovered and where to position the tooltip.
 * Coordinates are relative to the chart's wrapping container (pixels).
 */
interface TooltipState {
  segment: Segment
  left: number
  top: number
}

/**
 * StackedBarChart renders a responsive stacked bar chart as plain SVG.
 *
 * D3 is used only for the maths (scales, the stack layout, tick/label
 * formatting); React renders the resulting `<rect>`/`<text>` elements.
 *
 * Each bar is one category; each stacked segment is one series. Hovering a
 * segment shows a tooltip with the series label and the exact value.
 *
 * @param props - See StackedBarChartProps.
 * @returns The chart as a responsive, accessible SVG with a hover tooltip.
 */
export default function StackedBarChart({
  data,
  seriesKeys,
  colorFor,
  formatXLabel,
  formatSeriesLabel,
  height = 240,
  valueUnit,
}: StackedBarChartProps): JSX.Element {
  // The currently hovered segment and where to draw its tooltip, or null.
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  // All layout maths run once per data/series change. The output is a plain
  // list of rectangles, axis ticks, and x labels that React renders directly.
  const { segments, ticks, xLabels, innerHeight } = useMemo(() => {
    const innerWidth = VIEWBOX_WIDTH - MARGIN.left - MARGIN.right
    const plotHeight = height - MARGIN.top - MARGIN.bottom

    // X scale — one band per category.
    const xScale = scaleBand<string>()
      .domain(data.map((d) => d.label))
      .range([0, innerWidth])
      .padding(0.2)

    // Tallest stacked total sets the top of the y axis. A zero max
    // falls back to 1 so the axis and scale stay valid.
    const maxTotal =
      max(data, (d) =>
        seriesKeys.reduce((sum, key) => sum + (d.values[key] ?? 0), 0)
      ) ?? 0

    const yScale = scaleLinear()
      .domain([0, maxTotal > 0 ? maxTotal : 1])
      .nice()
      .range([plotHeight, 0])

    // Stack the series;
    const stackGenerator = stack<StackedBarDatum>()
      .keys(seriesKeys)
      .value((d, key) => d.values[key] ?? 0)

    const layers = stackGenerator(data)

    // Flatten the stack into individual rectangles for rendering.
    const rects: Segment[] = []
    for (const layer of layers) {
      for (const point of layer) {
        const x = xScale(point.data.label)
        if (x === undefined) {
          continue
        }
        rects.push({
          key: `${layer.key}-${point.data.label}`,
          seriesKey: layer.key,
          label: point.data.label,
          value: point.data.values[layer.key] ?? 0,
          x,
          y: yScale(point[1]),
          width: xScale.bandwidth(),
          height: Math.max(0, yScale(point[0]) - yScale(point[1])),
          fill: colorFor(layer.key),
        })
      }
    }

    const resolvedTicks: Tick[] = yScale
      .ticks(4)
      .map((value) => ({ value, y: yScale(value) }))

    const resolvedXLabels = data.map((d) => ({
      label: d.label,
      // Centre the label under its band.
      x: (xScale(d.label) ?? 0) + xScale.bandwidth() / 2,
    }))

    return {
      segments: rects,
      ticks: resolvedTicks,
      xLabels: resolvedXLabels,
      innerHeight: plotHeight,
    }
  }, [data, seriesKeys, colorFor, height])

  /**
   * Update the tooltip position from a pointer event over a segment.
   *
   * Positions the tooltip relative to the wrapping container using the mouse
   * coordinates within it, so it follows the cursor regardless of the SVG's
   * responsive scaling.
   *
   * @param event - The React pointer/mouse event on the segment rect.
   * @param segment - The hovered segment.
   */
  function handleSegmentHover(
    event: React.MouseEvent<SVGRectElement>,
    segment: Segment
  ): void {
    // currentTarget's owner container is the relative wrapper (the <div>).
    const container = event.currentTarget.ownerSVGElement?.parentElement
    if (!container) {
      return
    }
    const bounds = container.getBoundingClientRect()
    setTooltip({
      segment,
      left: event.clientX - bounds.left,
      top: event.clientY - bounds.top,
    })
  }

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label="Stacked bar chart"
        className="text-muted-foreground"
      >
        {/* Plot area, offset by the margins. */}
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {/* Y-axis gridlines + tick labels. */}
          {ticks.map((tick) => (
            <g key={tick.value}>
              <line
                x1={0}
                x2={VIEWBOX_WIDTH - MARGIN.left - MARGIN.right}
                y1={tick.y}
                y2={tick.y}
                stroke="currentColor"
                strokeOpacity={0.15}
              />
              <text
                x={-8}
                y={tick.y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={11}
                fill="currentColor"
              >
                {formatTick(tick.value)}
              </text>
            </g>
          ))}

          {/* Stacked bar segments. Zero-value segments are skipped so they
              neither render nor capture hover. */}
          {segments.map((segment) =>
            segment.value > 0 ? (
              <rect
                key={segment.key}
                x={segment.x}
                y={segment.y}
                width={segment.width}
                height={segment.height}
                fill={segment.fill}
                onMouseMove={(event) => handleSegmentHover(event, segment)}
                onMouseLeave={() => setTooltip(null)}
              />
            ) : null
          )}

          {/* X-axis labels, centred under each bar. */}
          {xLabels.map((item) => (
            <text
              key={item.label}
              x={item.x}
              y={innerHeight + 18}
              textAnchor="middle"
              fontSize={11}
              fill="currentColor"
            >
              {formatXLabel ? formatXLabel(item.label) : item.label}
            </text>
          ))}
        </g>
      </svg>

      {/* Hover tooltip — an absolutely-positioned overlay following the cursor. */}
      {tooltip && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border bg-popover px-2 py-1 text-xs shadow-md"
          style={{ left: tooltip.left, top: tooltip.top - 8 }}
          role="tooltip"
        >
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block size-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: tooltip.segment.fill }}
              aria-hidden="true"
            />
            <span className="font-medium">
              {formatSeriesLabel
                ? formatSeriesLabel(tooltip.segment.seriesKey)
                : tooltip.segment.seriesKey}
            </span>
          </div>
          <div className="mt-0.5 text-muted-foreground">
            {formatXLabel
              ? formatXLabel(tooltip.segment.label)
              : tooltip.segment.label}
            {": "}
            {formatExact(tooltip.segment.value)}
            {valueUnit ? ` ${valueUnit}` : ""}
          </div>
        </div>
      )}
    </div>
  )
}
